// Adapter: toolbox assessment answers -> AI-Era report engine inputs.
//
// Pure. No React, no fetching. Given the flat `answersByKey` map the toolbox
// assessment collects (keyed by question key, the same keys `visible_when` and
// the maturity scorer use) plus the already-fetched inventory rows from the
// `data_import` question's dataset, it produces the three inputs the ported
// engine consumes:
//   - MultiAssessmentInput  (use-cases + org-wide env + maturity + tech-adoption)
//   - EntitySelection[]     (Cisco product inventory, matched against the catalog)
//   - Branding              (org name + industry profile for the report cover)
//
// The toolbox seed collects everything the engine needs, but in a flatter shape:
// env is asked once org-wide (not per use-case), tech-adoption is stored as flat
// `platform__field` keys (not nested), and the inventory arrives as raw rows.
// This module reconciles those shape gaps; nothing here changes what is scored.

import {
  DEFAULT_BRANDING,
  type AutonomyLevel,
  type Branding,
  type EntitySelection,
  type EnvAnswers,
  type EnvDimensionId,
  type MaturityAnswers,
  type MultiAssessmentInput,
  type TechAdoptionAnswers,
  type UseCaseId,
  type UseCaseSelection,
} from './shared/types';
import { deriveAdoptionMaturity } from './scoring/derive-tech-adoption';
import { deriveUseCaseIds } from '../dataImport/catalog/entityCatalog';
import { matchLabel } from '../dataImport/catalog/match';
import { resolveRows } from '../dataImport/catalog/resolve';

/** The three engine inputs built from a toolbox submission. */
export type AiEraInputs = {
  input: MultiAssessmentInput;
  entities: EntitySelection[];
  branding: Branding;
};

// Maturity control keys look like `D1.SD2.C1`. The seed uses these verbatim as
// the maturity questions' keys, so they land straight in `answersByKey`.
const CONTROL_KEY = /^D\d+\.SD\d+\.C\d+$/;

const ENV_DIMENSIONS: EnvDimensionId[] = [
  'network_complexity',
  'cloud_onprem_mix',
  'ai_agents_deployed',
];

const VALID_USE_CASE_IDS: ReadonlySet<UseCaseId> = new Set<UseCaseId>([
  'nexus_dc',
  'meraki',
  'catalyst_sdwan',
  'catalyst_switching',
  'cisco_firewall',
  'collaboration',
  'wifi',
  'other_non_cisco',
]);

const isBlank = (v: any): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

// ---------------------------------------------------------------------------
// Maturity — explicit control answers, numeric-coerced. Blank answers are
// dropped (not defaulted to a level) so a derived value can fill the gap.
// ---------------------------------------------------------------------------
function readExplicitMaturity(answersByKey: Record<string, any>): MaturityAnswers {
  const out: MaturityAnswers = {};
  for (const [key, value] of Object.entries(answersByKey)) {
    if (!CONTROL_KEY.test(key) || isBlank(value)) continue;
    const n = Number(Array.isArray(value) ? value[0] : value);
    if (Number.isFinite(n)) out[key] = n;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Tech-adoption — flat `platform__field` keys reshaped to nested
// `{ platform: { field: value } }`, the shape deriveAdoptionMaturity reads.
// Booleans stay boolean (the account/org gates), everything else is a string.
// ---------------------------------------------------------------------------
function readTechAdoption(answersByKey: Record<string, any>): TechAdoptionAnswers {
  const out: TechAdoptionAnswers = {};
  for (const [key, value] of Object.entries(answersByKey)) {
    const sep = key.indexOf('__');
    if (sep <= 0 || isBlank(value)) continue;
    const platform = key.slice(0, sep);
    const field = key.slice(sep + 2);
    if (!field) continue;
    (out[platform] ??= {})[field] = typeof value === 'boolean' ? value : String(value);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Use-cases — the `usecases` multi_choice selection, augmented with any
// technology footprints implied by the matched inventory, with the org-wide
// environment profile fanned out onto each (the engine keys env per use-case).
// ---------------------------------------------------------------------------
function readEnv(answersByKey: Record<string, any>): EnvAnswers {
  const env: EnvAnswers = {};
  for (const dim of ENV_DIMENSIONS) {
    const v = answersByKey[dim];
    if (!isBlank(v)) env[dim] = String(v);
  }
  return env;
}

function readUseCases(
  answersByKey: Record<string, any>,
  entities: EntitySelection[],
): UseCaseSelection[] {
  const env = readEnv(answersByKey);
  const selected = new Set<UseCaseId>();

  const raw = answersByKey.usecases;
  const rawList = Array.isArray(raw) ? raw : isBlank(raw) ? [] : [raw];
  for (const id of rawList) {
    if (VALID_USE_CASE_IDS.has(id as UseCaseId)) selected.add(id as UseCaseId);
  }
  // Auto-select the technology-footprint domains the uploaded inventory implies.
  for (const id of deriveUseCaseIds(entities)) selected.add(id);

  return [...selected].map((id) => ({ id, env }));
}

function readAutonomy(answersByKey: Record<string, any>): AutonomyLevel | undefined {
  const raw = answersByKey.autonomyLevel;
  if (isBlank(raw)) return undefined;
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return undefined;
  const clamped = Math.max(1, Math.min(5, n));
  return clamped as AutonomyLevel;
}

function readBranding(answersByKey: Record<string, any>): Branding {
  // The customer-name question is an `sf_account` lookup (commits a `{id, name}`
  // SalesforceRef), but older submissions stored a plain string. Accept both: use
  // the ref's `.name` for the cover, and retain `.id` — the Salesforce AccountId
  // the Cisco Ready file upload attaches to.
  const rawOrg = answersByKey.orgName;
  const orgRef =
    rawOrg && typeof rawOrg === 'object' && !Array.isArray(rawOrg) ? rawOrg : null;
  const orgNameValue = orgRef ? orgRef.name : rawOrg;
  const orgName = isBlank(orgNameValue) ? '' : String(orgNameValue);
  const orgAccountId = orgRef && !isBlank(orgRef.id) ? String(orgRef.id) : undefined;

  const industryProfile = isBlank(answersByKey.industryProfile)
    ? DEFAULT_BRANDING.industryProfile
    : String(answersByKey.industryProfile);
  return { ...DEFAULT_BRANDING, orgName, orgAccountId, industryProfile };
}

// ---------------------------------------------------------------------------
// Inventory rows -> matched catalog EntitySelection[]. Mirrors the Cisco-inventory
// preset (dataImport/presets/ciscoInventory.ts): find the column whose values
// best match the catalog, carry a category-ish column through as rawCategory,
// then resolve exact/alias hits. Fuzzy-only rows stay unmatched (never trusted
// silently) — same rule the interactive importer uses.
// ---------------------------------------------------------------------------
const CATEGORY_COLUMN_ALIASES = ['category', 'group', 'family', 'architecture', 'row labels'];

function collectColumns(rows: any[]): string[] {
  const cols = new Set<string>();
  for (const row of rows.slice(0, 50)) {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => cols.add(k));
    }
  }
  return [...cols];
}

function columnMatchRate(rows: any[], column: string): number {
  let hits = 0;
  let considered = 0;
  for (const row of rows) {
    const raw = row?.[column];
    if (isBlank(raw)) continue;
    considered += 1;
    const candidates = matchLabel(String(raw));
    if (candidates.some((c) => c.confidence === 'exact' || c.confidence === 'alias')) hits += 1;
  }
  return considered ? hits / considered : 0;
}

export function rowsToEntitySelections(rows: any[]): EntitySelection[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const columns = collectColumns(rows);
  if (columns.length === 0) return [];

  let itemColumn: string | null = null;
  let bestRate = 0;
  for (const column of columns) {
    const rate = columnMatchRate(rows, column);
    if (rate > bestRate) {
      bestRate = rate;
      itemColumn = column;
    }
  }
  if (!itemColumn) return [];

  const categoryColumn =
    columns.find(
      (c) => c !== itemColumn && CATEGORY_COLUMN_ALIASES.includes(c.trim().toLowerCase()),
    ) ?? null;

  const rawRows = rows
    .map((row) => ({
      label: String(row?.[itemColumn as string] ?? '').trim(),
      rawCategory: categoryColumn ? String(row?.[categoryColumn] ?? '').trim() : undefined,
    }))
    .filter((r) => r.label);

  const { matched } = resolveRows(rawRows);
  const addedAt = new Date().toISOString();
  return matched.map((m) => ({
    itemId: m.itemId,
    source: 'excel',
    confidence: m.confidence,
    rawLabel: m.rawLabel,
    addedAt,
  }));
}

// ---------------------------------------------------------------------------
// Entry point.
// ---------------------------------------------------------------------------
export function buildAiEraInputs(
  answersByKey: Record<string, any>,
  inventoryRows: any[] = [],
): AiEraInputs {
  const entities = rowsToEntitySelections(inventoryRows);
  const useCases = readUseCases(answersByKey, entities);
  const techAdoption = readTechAdoption(answersByKey);
  const explicitMaturity = readExplicitMaturity(answersByKey);

  // Derived controls (the 4 DERIVED_CONTROL_IDS) fill in from tech-adoption
  // evidence; an explicit answer for the same control always wins.
  const maturity: MaturityAnswers = {
    ...deriveAdoptionMaturity(techAdoption, useCases),
    ...explicitMaturity,
  };

  const input: MultiAssessmentInput = {
    useCases,
    maturity,
    techAdoption,
    autonomyLevel: readAutonomy(answersByKey),
  };

  return { input, entities, branding: readBranding(answersByKey) };
}
